import React from 'react';
import { formatCalendarDate } from '../utils/timezone';
// Version 3.4 - 2024-11-08 - Added category compliance table

interface Class {
  _id: string;
  classname: string;
  classdate: string;
  classLU: number;
  took?: boolean;
}

interface CategoryCompliance {
  categoryTag: string;
  categoryName?: string;
  description?: string;
  minCredits: number;
  maxCredits?: number;
  earned: number;
  planned?: number;
}

interface CyclePrintViewProps {
  cycleName: string;
  state: string;
  period: string;
  creditsRequired: number;
  totalLUCompleted: number;
  totalLUPlanned: number;
  luToBeCompleted: number;
  attachedClasses: Class[];
  firstName?: string;
  lastName?: string;
  professionName?: string;
  licenseNumber?: string;
  creditTerm?: string; // e.g., "CEC", "PDH", "LU"
  categoryCompliance?: CategoryCompliance[]; // Optional category breakdown
  isDentistProfession?: boolean; // Simplified tracking for dentists and dental hygienists
  note?: string; // Optional cycle memo — printed at end of report
}

const formatDate = (d: string | Date) => {
  try {
    return formatCalendarDate(d);
  } catch {
    return String(d);
  }
};

const printStyles = `
  /* CRITICAL: Table header must be black - both screen and print */
  .print-table-header {
    display: grid !important;
    grid-template-columns: 1fr 120px 100px 80px !important;
    gap: 0 !important;
    font-size: 10pt !important;
    font-weight: bold !important;
    background-color: #000 !important;
    color: #fff !important;
    padding: 8px 0 !important;
  }

  @media print {
    @page {
      size: letter;
      margin: 0.5in 0.5in 0.75in 0.5in;
    }
    /* Force all background colors and images to print */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body {
      margin: 0;
      padding: 0;
      font-size: 11pt !important;
    }
    .print-container {
      width: 100%;
      margin: 0;
      padding: 0;
      position: relative;
      min-height: 100vh;
      font-size: 11pt !important;
    }
    .print-table-header {
      display: grid !important;
      grid-template-columns: 1fr 120px 100px 80px !important;
      background-color: #000 !important;
      color: #fff !important;
    }
    .page-break {
      page-break-after: always;
      break-after: page;
    }
    .avoid-break {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .print-footer {
      display: none !important;
    }
  }
  
  @media screen {
    .print-container {
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0.5in;
      background: white;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      min-height: 11in;
      page-break-after: always;
    }
    .page-break {
      margin: 30px 0;
      padding: 20px 0;
      border-top: 2px dashed #ccc;
      border-bottom: 2px dashed #ccc;
      background: #f9f9f9;
      text-align: center;
      color: #999;
      font-size: 12px;
      font-weight: bold;
    }
    .page-break::before {
      content: "— Page Break —";
    }
    .print-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid #ccc;
    }
  }
`;

export const CyclePrintView: React.FC<CyclePrintViewProps> = ({
  cycleName,
  state,
  period,
  creditsRequired,
  totalLUCompleted,
  totalLUPlanned,
  luToBeCompleted,
  attachedClasses,
  firstName,
  lastName,
  professionName,
  licenseNumber,
  creditTerm = 'CEC',
  categoryCompliance,
  isDentistProfession = false,
  note,
}) => {
  const reportDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return (
    <>
      <style>{printStyles}</style>
      <div className="print-container" style={{ 
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        fontSize: '11pt',
        lineHeight: '1.4',
        color: '#000',
      }}>
        {/* Header - Left: Cycle Name, Right: Report Date (aligned with page numbers below) */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '8px',
          paddingBottom: '8px',
          borderBottom: '1px solid #000',
        }}>
          <div style={{ 
            fontSize: '14pt', 
            fontWeight: 'bold',
            textAlign: 'left',
            flex: '1',
          }}>
            {cycleName}
          </div>
          <div style={{ 
            fontSize: '9pt',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}>
            {reportDate}
          </div>
        </div>

        {/* Title */}
        <div style={{ 
          fontSize: '16pt', 
          fontWeight: 'bold',
          marginTop: '12px',
          marginBottom: '16px',
        }}>
          Continuing Education Report
        </div>

        {/* Line 1 */}
        <div style={{ 
          borderTop: '1px solid #000',
          marginBottom: '12px',
        }}></div>

        {/* Licensee Information - Three columns */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '8px',
          fontSize: '10pt',
        }}>
          <div style={{ flex: '1 1 33%' }}>
            <span style={{ fontWeight: 'bold' }}>Profession:</span> {professionName || 'N/A'}
          </div>
          <div style={{ flex: '1 1 33%', paddingLeft: '20px' }}>
            <span style={{ fontWeight: 'bold' }}>Name:</span> {fullName || 'N/A'}
          </div>
          <div style={{ flex: '1 1 33%', paddingLeft: '20px' }}>
            <span style={{ fontWeight: 'bold' }}>License No. ({state}):</span> {licenseNumber || 'N/A'}
          </div>
        </div>

        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '12px',
          fontSize: '10pt',
        }}>
          <div style={{ flex: '1 1 50%' }}>
            <span style={{ fontWeight: 'bold' }}>Cycle:</span> {state} - {period}
          </div>
          <div style={{ flex: '1 1 50%', paddingLeft: '20px' }}>
            <span style={{ fontWeight: 'bold' }}>Credits Required:</span> {creditsRequired} {creditTerm}
          </div>
        </div>

        {/* Line 2 */}
        <div style={{ 
          borderTop: '1px solid #000',
          marginBottom: '12px',
        }}></div>

        {/* Summary Stats */}
        <div style={{ 
          marginBottom: '12px',
          fontSize: '10pt',
          fontWeight: 'bold',
        }}>
          Completed: {totalLUCompleted} {creditTerm} | Planned: {totalLUPlanned} {creditTerm} | To Plan: {luToBeCompleted} {creditTerm}
        </div>

        {/* Line 3 */}
        <div style={{ 
          borderTop: '1px solid #000',
          marginBottom: '16px',
        }}></div>

        {/* Category Compliance Section - Optional */}
        {categoryCompliance && categoryCompliance.length > 0 && (
          <>
            {/* Category Section Title */}
            <div style={{ 
              fontSize: '12pt',
              fontWeight: 'bold',
              marginBottom: '8px',
            }}>
              {isDentistProfession ? 'Continuing Education Requirements' : 'Category Requirements'}
            </div>

            {/* Line before category table */}
            <div style={{ 
              borderTop: '1px solid #000',
              marginBottom: '12px',
            }}></div>

            {isDentistProfession ? (
              /* Simplified display for dentist professions */
              <>
                <div style={{ marginBottom: '12px', fontSize: '10pt' }}>
                  <strong>NY Dentist Requirements:</strong> Any approved subject matter from recognized providers. 
                  Only delivery method matters: minimum 42 hours live/interactive, maximum 18 hours self-study.
                </div>
                
                {/* Dentist Simplified Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 120px 120px 120px',
                  gap: '0',
                  fontSize: '10pt',
                  fontWeight: 'bold',
                  backgroundColor: '#000',
                  color: '#fff',
                  padding: '8px 0',
                }}>
                  <div style={{ paddingLeft: '8px' }}>Requirement</div>
                  <div style={{ textAlign: 'center' }}>Required/Max</div>
                  <div style={{ textAlign: 'center' }}>Earned</div>
                  <div style={{ textAlign: 'center' }}>Status</div>
                </div>

                {/* Total Hours Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 120px 120px 120px',
                  gap: '0',
                  padding: '6px 0',
                  fontSize: '10pt',
                  backgroundColor: '#fff',
                }}>
                  <div style={{ paddingLeft: '8px' }}><strong>Total Hours</strong></div>
                  <div style={{ textAlign: 'center' }}>{creditsRequired} hours</div>
                  <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{totalLUCompleted}</div>
                  <div style={{ textAlign: 'center' }}>
                    {totalLUCompleted >= creditsRequired ? '✓ Complete' : `Need ${(creditsRequired - totalLUCompleted).toFixed(1)}`}
                  </div>
                </div>

                {/* Live/Interactive Row */}
                {(() => {
                  const liveEarned = categoryCompliance.find(c => c.categoryTag === 'LIVE_INTERACTIVE')?.earned || 0;
                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 120px 120px 120px',
                      gap: '0',
                      padding: '6px 0',
                      fontSize: '10pt',
                      backgroundColor: '#e8e8e8',
                    }}>
                      <div style={{ paddingLeft: '8px' }}><strong>Live/Interactive</strong></div>
                      <div style={{ textAlign: 'center' }}>42 min</div>
                      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{liveEarned}</div>
                      <div style={{ textAlign: 'center' }}>
                        {liveEarned >= 42 ? '✓ Complete' : `Need ${(42 - liveEarned).toFixed(1)}`}
                      </div>
                    </div>
                  );
                })()}

                {/* Self-Study Row */}
                {(() => {
                  const selfStudyEarned = categoryCompliance.find(c => c.categoryTag === 'SELF_STUDY')?.earned || 0;
                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 120px 120px 120px',
                      gap: '0',
                      padding: '6px 0',
                      fontSize: '10pt',
                      backgroundColor: '#fff',
                    }}>
                      <div style={{ paddingLeft: '8px' }}><strong>Self-Study</strong></div>
                      <div style={{ textAlign: 'center' }}>18 max</div>
                      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{selfStudyEarned}</div>
                      <div style={{ textAlign: 'center' }}>
                        {selfStudyEarned <= 18 ? '✓ Within Limit' : `Exceeds by ${(selfStudyEarned - 18).toFixed(1)}`}
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              /* Original detailed category table for other professions */
              <>
                {/* Category Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 100px 100px 120px',
                  gap: '0',
                  fontSize: '10pt',
                  fontWeight: 'bold',
                  backgroundColor: '#000',
                  color: '#fff',
                  padding: '8px 0',
                }}>
                  <div style={{ paddingLeft: '8px' }}>Category</div>
                  <div style={{ textAlign: 'center' }}>Required</div>
                  <div style={{ textAlign: 'center' }}>Earned</div>
                  <div style={{ textAlign: 'center' }}>Status</div>
                </div>

                {/* Category Rows */}
                {categoryCompliance
                  .filter(cat => !['INFECTION_CONTROL', 'PAIN_MANAGEMENT', 'DEA_MATERIAL'].includes(cat.categoryTag))
                  .map((cat, idx) => {
                  const isComplete = cat.minCredits > 0 && cat.earned >= cat.minCredits;
                  const total = cat.earned + (cat.planned || 0);
                  const isPlannedComplete = (cat.planned || 0) > 0 && total >= cat.minCredits;
                  
                  return (
                    <div 
                      key={cat.categoryTag}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 100px 100px 120px',
                        gap: '0',
                        padding: '6px 0',
                        fontSize: '10pt',
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#e8e8e8',
                      }}
                    >
                      <div style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                        <strong>{cat.categoryTag}</strong>
                        {cat.description && (
                          <span style={{ fontWeight: 'normal', color: '#666', fontSize: '9pt' }}> - {cat.description}</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'center' }}>{cat.minCredits}</div>
                      <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{cat.earned}</div>
                      <div style={{ textAlign: 'center' }}>
                        {isComplete ? '✓ Complete' : (isPlannedComplete && cat.planned ? '📅 Planned' : `Need ${(cat.minCredits - cat.earned).toFixed(1)}`)}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Line after category table */}
            <div style={{ 
              borderTop: '1px solid #000',
              marginTop: '12px',
              marginBottom: '16px',
            }}></div>
          </>
        )}

        {/* Section Title */}
        <div style={{ 
          fontSize: '12pt',
          fontWeight: 'bold',
          marginBottom: '8px',
        }}>
          Classes in This Cycle
        </div>

        {/* Line 4 */}
        <div style={{ 
          borderTop: '1px solid #000',
          marginBottom: '12px',
        }}></div>

        {/* Table Header - Black background with white text - using CSS class instead of inline */}
        <div className="print-table-header">
          <div style={{ paddingLeft: '8px' }}>Class Name</div>
          <div style={{ textAlign: 'center' }}>{creditTerm}</div>
          <div style={{ textAlign: 'center' }}>Status</div>
          <div style={{ textAlign: 'center' }}>Date</div>
        </div>

        {/* Table Rows - Alternating background: white and light gray */}
        {attachedClasses.length === 0 ? (
          <div style={{ 
            padding: '16px',
            textAlign: 'center',
            fontStyle: 'italic',
            color: '#666',
            fontSize: '10pt',
            backgroundColor: '#fff',
          }}>
            No classes attached to this cycle
          </div>
        ) : (
          attachedClasses.map((cls, idx) => (
            <div 
              key={cls._id}
              className="avoid-break"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 100px 80px',
                gap: '0',
                padding: '6px 0',
                fontSize: '10pt',
                backgroundColor: idx % 2 === 0 ? '#fff' : '#e8e8e8',
              }}
            >
              <div style={{ paddingLeft: '8px', paddingRight: '8px' }}>{cls.classname}</div>
              <div style={{ textAlign: 'center' }}>{cls.classLU}</div>
              <div style={{ textAlign: 'center' }}>{cls.took ? 'Completed' : 'Planned'}</div>
              <div style={{ textAlign: 'center' }}>{formatDate(cls.classdate)}</div>
            </div>
          ))
        )}

        {/* Line after class table */}
        <div style={{ 
          borderTop: '1px solid #000',
          marginTop: '12px',
        }}></div>

        {/* Cycle Notes (only if present) */}
        {note && (
          <div style={{ marginTop: '20px', marginBottom: '8px' }}>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>Cycle Notes</div>
            <div style={{ borderTop: '1px solid #ccc', paddingTop: '8px', fontSize: '10pt', color: '#444', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
              {note}
            </div>
          </div>
        )}

        {/* Footer - All inline with separator above - FLATTENED STRUCTURE */}
        <div 
          className="print-footer"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '9pt',
            color: '#666',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid #000',
          }}
        >
          <span>© {new Date().getFullYear()} CEC Track, Inc.</span>
          <span style={{ fontSize: '7pt', color: '#ccc' }}>v3.3-20241106-1910</span>
          <span style={{ fontSize: '9pt', color: '#666' }}></span>
        </div>
      </div>
    </>
  );
};
